import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import Button from '../../components/common/Button/Button';
import { useSearch } from '../../hooks/useSearch';
import { useAuth } from '../../hooks/useAuth';
import { TaskStatus } from '../../types/search.types';
import { PaymentFailedAlert } from '../../components/common/PaymentFailedAlert/PaymentFailedAlert';
import { subscriptionsApi } from '../../api/subscriptions';
import { usePostHog } from 'posthog-js/react';
import { balanceApi } from '../../api/balance';
import { BalanceResponse } from '../../types/balance.types';

import {
  Search,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  BarChart3,
  List,
  Zap,
  LayoutGrid,
  Star,
  TrendingUp,
  Mail,
  Users,
  MapPin,
  Download,
} from 'lucide-react';

const POPULAR_SEARCHES = [
  'Marketing agency',
  'Dentist',
  'Electrician',
  'Real estate',
  'Plumber',
  'Accountant',
];

const Dashboard: React.FC = () => {
  const { searches, loading, fetchSearches, statistics } = useSearch();
  const { isAuthenticated, user } = useAuth();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    running: 0,
    failed: 0,
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('');
  const [currentPlanName, setCurrentPlanName] = useState<string>('discover');
  const [dailySearchAllowance, setDailySearchAllowance] = useState<number | null>(null);
  const [maxResultsAllowance, setMaxResultsAllowance] = useState<number | null>(null);
  const [hasLoadedSubscription, setHasLoadedSubscription] = useState(false);
  const [isDiscoverBannerDismissed, setIsDiscoverBannerDismissed] = useState(false);
  const [nextPaymentAttempt, setNextPaymentAttempt] = useState<number | null>(null);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const bannerViewedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!isAuthenticated) {
        setHasLoadedSubscription(false);
        return;
      }

      try {
        const response = await subscriptionsApi.getCurrentSubscription();
        setSubscriptionStatus(response.subscription?.status ?? '');
        setCurrentPlanName(response.subscription?.plan_name || 'discover');
        const allowances = response.subscription?.allowances;
        setDailySearchAllowance(
          typeof allowances?.daily_searches === 'number' ? allowances.daily_searches : null
        );
        setMaxResultsAllowance(
          typeof allowances?.max_results === 'number' ? allowances.max_results : null
        );
        setNextPaymentAttempt(response.subscription?.next_payment_attempt || null);
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
      } finally {
        setHasLoadedSubscription(true);
      }
    };

    fetchSubscriptionStatus();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    balanceApi.getBalance().then(setBalance).catch(() => {});
  }, [isAuthenticated]);

  const hasLoaded = useRef(false);
  const fetchTimeoutRef = useRef<number | null>(null);
  const previousUserIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    const currentUserId = user?.id || null;
    const userChanged = previousUserIdRef.current !== null &&
                         previousUserIdRef.current !== currentUserId;

    if (userChanged) {
      hasLoaded.current = false;
      setStats({ total: 0, completed: 0, running: 0, failed: 0 });
      setBalance(null);
    }

    previousUserIdRef.current = currentUserId;

    if ((!hasLoaded.current || userChanged) && isAuthenticated) {
      if (fetchTimeoutRef.current !== null) {
        window.clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = window.setTimeout(() => {
        fetchSearches(undefined);
        hasLoaded.current = true;
        fetchTimeoutRef.current = null;
      }, 300);
    }

    return () => {
      if (fetchTimeoutRef.current !== null) {
        window.clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [fetchSearches, isAuthenticated, user]);

  useEffect(() => {
    if (statistics) {
      setStats({
        total: statistics.total_completed + statistics.total_processing + statistics.total_failed,
        completed: statistics.total_completed,
        running: statistics.total_processing,
        failed: statistics.total_failed,
      });
    } else if (searches.length > 0) {
      const newStats = {
        total: searches.length,
        completed: searches.filter(s => s.status === TaskStatus.COMPLETED).length,
        running: searches.filter(s => [TaskStatus.QUEUED, TaskStatus.PENDING, TaskStatus.PROCESSING].includes(s.status)).length,
        failed: searches.filter(s => [TaskStatus.FAILED].includes(s.status)).length,
      };
      setStats(newStats);
    } else {
      setStats({ total: 0, completed: 0, running: 0, failed: 0 });
    }
  }, [searches, statistics]);

  const recentSearches = [...searches]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const discoverBannerStorageKey = user?.id
    ? `dashboard-discover-banner:v2:user:${user.id}:plan:${currentPlanName}`
    : null;

  useEffect(() => {
    if (!discoverBannerStorageKey) {
      setIsDiscoverBannerDismissed(false);
      return;
    }
    setIsDiscoverBannerDismissed(localStorage.getItem(discoverBannerStorageKey) === '1');
  }, [discoverBannerStorageKey]);

  const shouldShowDiscoverBanner =
    isAuthenticated && hasLoadedSubscription && currentPlanName === 'discover' && !isDiscoverBannerDismissed;

  const bannerPlacement = recentSearches.length === 0 ? 'empty_state' : 'post_search';

  useEffect(() => {
    if (!shouldShowDiscoverBanner) return;
    if (loading) return;
    const impressionKey = `${user?.id || 'anon'}-${bannerPlacement}`;
    if (bannerViewedRef.current.has(impressionKey)) return;
    bannerViewedRef.current.add(impressionKey);
    posthog?.capture('dashboard_upgrade_banner_viewed', {
      context: 'dashboard_discover_nudge',
      placement: bannerPlacement,
      current_plan: currentPlanName,
      daily_searches: dailySearchAllowance,
      max_results: maxResultsAllowance,
      max_queued_searches: null,
      recommended_plan: null,
      recommended_limit: null,
      active_searches: null,
      locked_places_count: null,
      search_id: null,
    });
  }, [
    shouldShowDiscoverBanner,
    loading,
    bannerPlacement,
    posthog,
    currentPlanName,
    dailySearchAllowance,
    maxResultsAllowance,
    user,
  ]);

  const handleDismissDiscoverBanner = () => {
    if (discoverBannerStorageKey) {
      localStorage.setItem(discoverBannerStorageKey, '1');
    }
    setIsDiscoverBannerDismissed(true);
    posthog?.capture('dashboard_upgrade_banner_dismissed', {
      context: 'dashboard_discover_nudge',
      placement: bannerPlacement,
      current_plan: currentPlanName,
      daily_searches: dailySearchAllowance,
      max_results: maxResultsAllowance,
      max_queued_searches: null,
      recommended_plan: null,
      recommended_limit: null,
      active_searches: null,
      locked_places_count: null,
      search_id: null,
    });
  };

  const handleDiscoverBannerClick = () => {
    posthog?.capture('dashboard_upgrade_banner_clicked', {
      context: 'dashboard_discover_nudge',
      placement: bannerPlacement,
      current_plan: currentPlanName,
      daily_searches: dailySearchAllowance,
      max_results: maxResultsAllowance,
      max_queued_searches: null,
      recommended_plan: null,
      recommended_limit: null,
      active_searches: null,
      locked_places_count: null,
      search_id: null,
    });
  };

  const renderDiscoverBanner = () => {
    const searchesRemaining = balance?.searches_remaining ?? dailySearchAllowance;
    const allUsed = searchesRemaining !== null && searchesRemaining === 0;

    return (
      <div className="mb-4 rounded-lg border border-accent/40 bg-accent/10 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-primary">
              {allUsed
                ? "You've used all your free searches today"
                : searchesRemaining !== null
                  ? `${searchesRemaining} free search${searchesRemaining === 1 ? '' : 'es'} remaining today`
                  : 'You are on the Discover plan'}
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              {maxResultsAllowance !== null
                ? `Results are capped at ${maxResultsAllowance.toLocaleString()} per search on Discover.`
                : 'Your free plan shows part of each result set.'}
              {' '}Upgrade to unlock unlimited daily searches and full result sets.
            </p>
            <Link
              to="/subscriptions?scrollTo=plans"
              onClick={handleDiscoverBannerClick}
              className="mt-2 inline-block text-sm font-semibold text-primary hover:underline"
            >
              {allUsed ? 'Upgrade now →' : 'View plans →'}
            </Link>
          </div>
          <button
            type="button"
            className="shrink-0 text-xs text-text-secondary hover:text-primary"
            onClick={handleDismissDiscoverBanner}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  };

  const getStatusBadgeClass = (status: TaskStatus): string => {
    switch (status) {
      case TaskStatus.COMPLETED:
        return 'status-completed';
      case TaskStatus.QUEUED:
      case TaskStatus.PENDING:
      case TaskStatus.PROCESSING:
      case TaskStatus.DATA_COLLECTED:
      case TaskStatus.VALIDATION:
      case TaskStatus.EMAIL_DISCOVERY:
      case TaskStatus.NORMALIZATION:
        return 'status-processing';
      case TaskStatus.FAILED:
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isReady = !loading && hasLoadedSubscription;
  const isNewUser = isReady && currentPlanName === 'discover';
  const isReturningDiscoverUser = isNewUser && stats.total > 0;

  const searchesUsed = balance?.searches_used_today ?? 0;
  const searchesLimit = balance?.daily_search_limit ?? null;
  const searchesRemaining = balance?.searches_remaining ?? null;
  const isSearchesExhausted = isReturningDiscoverUser && searchesRemaining === 0;
  const progressPercent = searchesLimit ? Math.round((searchesUsed / searchesLimit) * 100) : 0;

  return (
    <PageContainer title="Dashboard" titleIcon={<LayoutGrid />}>
      <PaymentFailedAlert
        subscriptionStatus={subscriptionStatus}
        nextPaymentAttempt={nextPaymentAttempt}
      />

      {!isReady ? (
        <div className="flex justify-center items-center py-16 text-text-secondary">
          <RefreshCw className="animate-spin mr-3 h-5 w-5" />
          Loading...
        </div>
      ) : isNewUser ? (
        <div>
          {/* Hero banner */}
          <div className="rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 mb-6 flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
            <div className="flex-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-widest text-yellow-400 bg-white/10 rounded-full px-3 py-1 mb-4">
                <Star className="h-3 w-3" />
                WELCOME TO WEBLEADS
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
                {isSearchesExhausted
                  ? `Hey ${user?.username}, you've used all your free searches`
                  : isReturningDiscoverUser
                    ? `Hey ${user?.username}, you're on the free plan`
                    : `Hey ${user?.username}, let's find`}
              </h1>
              <h1 className="text-2xl md:text-3xl font-bold text-yellow-400 mb-4">
                {isSearchesExhausted
                  ? 'ready to unlock unlimited leads?'
                  : isReturningDiscoverUser
                    ? (searchesRemaining !== null
                        ? `${searchesRemaining} search${searchesRemaining === 1 ? '' : 'es'} left to discover leads.`
                        : 'make your searches count.')
                    : 'your first batch of leads.'}
              </h1>
              <p className="text-gray-300 text-sm mb-6 max-w-md">
                {isSearchesExhausted
                  ? 'Upgrade from $24/mo to get more searches and full contact enrichment.'
                  : isReturningDiscoverUser
                    ? 'Upgrade anytime to unlock unlimited searches and full contact enrichment.'
                    : (searchesLimit !== null
                        ? <><span className="text-yellow-400 font-semibold">{searchesLimit} free search{searchesLimit === 1 ? '' : 'es'}</span> ready to use. One search can return hundreds of enriched leads with emails and contact info.</>
                        : 'One search can return hundreds of enriched leads with emails and contact info.')}
              </p>
              {isSearchesExhausted ? (
                <Link to="/subscriptions?scrollTo=plans">
                  <Button variant="primary" className="font-semibold">
                    Upgrade Now &rarr;
                  </Button>
                </Link>
              ) : (
                <Link to="/searches/new">
                  <Button variant="primary" className="font-semibold">
                    {isReturningDiscoverUser ? '+ New Search' : '+ Create Your First Search'}
                  </Button>
                </Link>
              )}
            </div>

            {/* Searches widget */}
            <div className="bg-white/10 backdrop-blur rounded-xl p-5 min-w-[200px] text-white shrink-0">
              <p className="text-xs font-semibold tracking-widest text-gray-400 mb-2">SEARCHES USED TODAY</p>
              <p className="text-4xl font-bold mb-0.5">
                {searchesUsed}
                {searchesLimit !== null && (
                  <span className="text-xl font-normal text-gray-400"> / {searchesLimit}</span>
                )}
              </p>
              <div className="w-full bg-white/10 rounded-full h-1.5 my-3">
                <div
                  className="bg-yellow-400 h-1.5 rounded-full transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              {searchesRemaining !== null && (
                <p className="text-xs text-gray-400">≈ {searchesRemaining} searches available</p>
              )}
              {currentPlanName === 'discover' && hasLoadedSubscription && (
                <Link
                  to="/subscriptions?scrollTo=plans"
                  onClick={handleDiscoverBannerClick}
                  className="mt-3 block text-xs font-semibold text-yellow-400 hover:underline"
                >
                  Need more? From $24/mo &rarr;
                </Link>
              )}
            </div>
          </div>

          {shouldShowDiscoverBanner && renderDiscoverBanner()}

          {/* Bottom grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* How it works */}
            <div className="lg:col-span-2 card">
              <div className="flex justify-between items-center mb-6">
                <h3 className="flex items-center text-base font-semibold text-text-primary">
                  <TrendingUp className="h-4 w-4 mr-2 text-secondary" />
                  How it works
                </h3>
                <span className="text-xs text-text-secondary">3 simple steps</span>
              </div>

              <div className="space-y-6 mb-8">
                {[
                  {
                    n: 1,
                    title: 'Name your target',
                    desc: 'Try "dentist", "marketing agency", "electrician"',
                  },
                  {
                    n: 2,
                    title: 'Pick a location',
                    desc: 'City + state or country. WebLeads finds fresh leads in real-time.',
                  },
                  {
                    n: 3,
                    title: 'Get enriched leads',
                    desc: 'Emails, socials, and decision makers. Exported to CSV in minutes.',
                  },
                ].map(({ n, title, desc }) => (
                  <div key={n} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-secondary flex items-center justify-center text-sm font-bold text-secondary">
                      {n}
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary">{title}</p>
                      <p className="text-sm text-text-secondary mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {isSearchesExhausted ? (
                <Link to="/subscriptions?scrollTo=plans">
                  <Button variant="primary" fullWidth className="font-semibold">
                    Upgrade Now &rarr;
                  </Button>
                </Link>
              ) : (
                <Link to="/searches/new">
                  <Button variant="primary" fullWidth className="font-semibold">
                    {isReturningDiscoverUser ? '+ New Search' : '+ Start First Search'}
                  </Button>
                </Link>
              )}
            </div>

            {/* Right sidebar */}
            <div className="space-y-4">
              {/* Popular searches */}
              <div className="card">
                <h3 className="flex items-center text-base font-semibold text-text-primary mb-4">
                  <Star className="h-4 w-4 mr-2 text-secondary" />
                  Popular searches
                </h3>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_SEARCHES.map(query => (
                    <button
                      key={query}
                      onClick={() => navigate('/searches/new', { state: { search_query: query } })}
                      className="text-sm px-3 py-1.5 rounded-full border border-gray-200 text-text-secondary hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>

              {/* What each lead includes */}
              <div className="card">
                <h3 className="flex items-center text-base font-semibold text-text-primary mb-4">
                  <CheckCircle2 className="h-4 w-4 mr-2 text-status-success" />
                  What each lead includes
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm text-text-secondary">
                    <Mail className="h-4 w-4 text-blue-500 shrink-0" />
                    Email addresses
                  </li>
                  <li className="flex items-center gap-3 text-sm text-text-secondary">
                    <Users className="h-4 w-4 text-purple-500 shrink-0" />
                    Decision maker names &amp; roles
                  </li>
                  <li className="flex items-center gap-3 text-sm text-text-secondary">
                    <MapPin className="h-4 w-4 text-green-500 shrink-0" />
                    Address &amp; location data
                  </li>
                  <li className="flex items-center gap-3 text-sm text-text-secondary">
                    <Download className="h-4 w-4 text-yellow-500 shrink-0" />
                    CSV / Excel export
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 md:mb-8">
            <h2 className="flex items-center text-lg md:text-xl font-semibold text-text-primary mb-3 md:mb-4">
              <BarChart3 className="h-4 w-4 md:h-5 md:w-5 mr-2 text-secondary" />
              Dashboard Overview
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              <div className="dashboard-stat-primary">
                <div className="dashboard-stat-icon bg-primary/10">
                  <Search className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                </div>
                <p className="text-xs md:text-sm font-medium text-text-secondary">Total Searches</p>
                <p className="text-2xl md:text-3xl font-bold text-text-primary mt-1">{stats.total}</p>
              </div>

              <div className="dashboard-stat-success">
                <div className="dashboard-stat-icon bg-status-success/10">
                  <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6 text-status-success" />
                </div>
                <p className="text-xs md:text-sm font-medium text-text-secondary">Completed</p>
                <p className="text-2xl md:text-3xl font-bold text-text-primary mt-1">{stats.completed}</p>
              </div>

              <div className="dashboard-stat-map">
                <div className="dashboard-stat-icon bg-status-processing/10">
                  <RefreshCw className="h-5 w-5 md:h-6 md:w-6 text-status-processing" />
                </div>
                <p className="text-xs md:text-sm font-medium text-text-secondary">Processing</p>
                <p className="text-2xl md:text-3xl font-bold text-text-primary mt-1">{stats.running}</p>
              </div>

              <div className="dashboard-stat border-t-4 border-t-status-error">
                <div className="dashboard-stat-icon bg-status-error/10">
                  <AlertCircle className="h-5 w-5 md:h-6 md:w-6 text-status-error" />
                </div>
                <p className="text-xs md:text-sm font-medium text-text-secondary">Failed/Partial</p>
                <p className="text-2xl md:text-3xl font-bold text-text-primary mt-1">{stats.failed}</p>
              </div>
            </div>
          </div>

          {shouldShowDiscoverBanner && renderDiscoverBanner()}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2 card">
              <div className="flex justify-between items-center mb-3 md:mb-4">
                <h3 className="flex items-center text-base md:text-lg font-semibold text-text-primary">
                  <List className="h-4 w-4 md:h-5 md:w-5 mr-2 text-secondary" />
                  Recent Searches
                </h3>
                <Link to="/searches">
                  <Button variant="text" size="sm" className="text-primary hover:underline">View all</Button>
                </Link>
              </div>

              {!isAuthenticated ? (
                <div className="text-center py-8 text-text-secondary">
                  Please log in to view your searches.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {recentSearches.map(search => (
                    <li key={search.id} className="py-3 md:py-4 flex items-center justify-between hover:bg-light transition duration-150 -mx-3 md:-mx-6 px-3 md:px-6">
                      <div className="flex-1 min-w-0 mr-3">
                        <Link to={`/searches/${search.id}`} className="block hover:text-primary focus:outline-none focus:text-primary">
                          <p className="text-xs md:text-sm font-medium text-text-primary truncate">
                            {search.search_query}
                          </p>
                          <p className="text-xs md:text-sm text-text-secondary mt-1 truncate">
                            {search.city?.name && search.city?.state_name && search.city?.country
                              ? `${search.city.name}, ${search.city.state_name}, ${search.city.country}`
                              : 'Location data unavailable'}
                          </p>
                        </Link>
                        <div className="mt-2">
                          <span className={`status-badge text-xs ${getStatusBadgeClass(search.status)}`}>
                            {search.status === TaskStatus.EMAIL_DISCOVERY ? 'EMAIL ENRICHMENT' : search.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <Link to={`/searches/${search.id}`}>
                          <Button variant="secondary" size="sm" className="text-xs md:text-sm px-2 md:px-3">
                            View
                          </Button>
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card">
              <h3 className="flex items-center text-base md:text-lg font-semibold text-text-primary mb-3 md:mb-4">
                <Zap className="h-4 w-4 md:h-5 md:w-5 mr-2 text-secondary" />
                Quick Actions
              </h3>

              <div className="space-y-3">
                <Link to="/searches/new">
                  <Button variant="primary" fullWidth>
                    Create New Search
                  </Button>
                </Link>

                <Link to="/searches">
                  <Button variant="secondary" fullWidth>
                    View All Searches
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default Dashboard;
